import { Alert, Form, Input, Radio, Select, Space } from "antd";
import { useMemo } from "react";
import { useEmpreendimentosSystem } from "../../../hooks/useEmpreendimentosSystem";
import { Empreendimento } from "../../../interfaces/Empreendimento";
import { FluxoResposta } from "../cadastroCotaUsuario.types";
import { LocationFields } from "../components/LocationFields";
import { filterSelectOption, OnboardingFrame, SubmitButton } from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

export function EtapaEmpreendimento({ fluxo, submitting, onConcluir }: Props) {
  const [form] = Form.useForm();
  const modo = Form.useWatch("modo", form) ?? "EXISTENTE";
  const { data, isLoading } = useEmpreendimentosSystem();
  const dadosEmpreendimento =
    (fluxo.dadosPreenchidos?.empreendimento as Record<string, unknown> | undefined) ??
    {};

  const empreendimentos = useMemo(
    () => ((data ?? []) as Empreendimento[]).filter((item) => item.id),
    [data]
  );

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Vamos começar?"
      description="Escolha um empreendimento já existente no sistema ou cadastre um novo para iniciar."
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          modo: dadosEmpreendimento.modo ?? "EXISTENTE",
          empreendimentoId:
            dadosEmpreendimento.empreendimentoId ?? fluxo.contextoFluxo.empreendimentoId,
          nome: dadosEmpreendimento.nome,
          paisId: dadosEmpreendimento.paisId,
          estadoId: dadosEmpreendimento.estadoId,
          cidadeId: dadosEmpreendimento.cidadeId,
        }}
        onFinish={(values) => {
          if (values.modo === "EXISTENTE") {
            onConcluir({
              modo: "EXISTENTE",
              empreendimentoId: values.empreendimentoId,
            });
            return;
          }

          onConcluir({
            modo: "NOVO",
            nome: values.nome,
            cidadeId: values.cidadeId,
          });
        }}
      >
        <Form.Item
          label="Como você quer seguir?"
          name="modo"
          rules={[{ required: true, message: "Selecione uma opção." }]}
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="EXISTENTE">Selecionar empreendimento existente</Radio>
              <Radio value="NOVO">Cadastrar novo empreendimento</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {modo === "EXISTENTE" ? (
          <>
            {!empreendimentos.length && !isLoading ? (
              <Alert
                type="info"
                showIcon
                message="Nenhum empreendimento do sistema foi encontrado."
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Form.Item
              label="Empreendimento"
              name="empreendimentoId"
              rules={[{ required: true, message: "Selecione o empreendimento." }]}
            >
              <Select
                showSearch
                loading={isLoading}
                optionFilterProp="label"
                filterOption={filterSelectOption}
                options={empreendimentos.map((empreendimento) => ({
                  label: empreendimento.nome ?? `Empreendimento ${empreendimento.id}`,
                  value: empreendimento.id,
                }))}
                placeholder="Escolha o empreendimento"
              />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              label="Nome do empreendimento"
              name="nome"
              rules={[{ required: true, message: "Informe o nome do empreendimento." }]}
            >
              <Input placeholder="Ex.: Resort das Araucárias" />
            </Form.Item>

            <LocationFields form={form} />
          </>
        )}

        <SubmitButton label="Continuar" loading={submitting} />
      </Form>
    </OnboardingFrame>
  );
}
